// The number behind a record raised from a dashboard: what it was and why when the record was raised, what it is
// now, and a way back to the dashboard. Shows nothing on records that weren't raised from a number.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, type NumberRef } from "../api";
import { fmtPlain, fmtRange, fmtSigned } from "./format";

const CMP = { py: "the prior year", pp: "the prior period" } as const;

export function NumberCard({ record, onRecordResult }: { record: string; onRecordResult?: (value: number) => Promise<void> }) {
  const [ref, setRef] = useState<NumberRef | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    let live = true;
    setRef(null); setSaved(false);
    api.actRef(record).then((r) => { if (live) setRef(r); }).catch(() => { if (live) setRef(null); });
    return () => { live = false; };
  }, [record]);
  if (!ref) return null;
  const f = ref.format;
  // Less than a cent (or a hundredth of a point) is no change: the two numbers come from separately rounded queries.
  const raw = ref.live && ref.live.value !== null && ref.value !== null ? ref.live.value - ref.value : null;
  const since = raw === null || Math.abs(raw) < (f === "pct" ? 5e-5 : 0.005) ? (raw === null ? null : 0) : raw;
  const better = since === null || since === 0 ? null : (since > 0) === (ref.good === "up");
  const raised = new Date(ref.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  const href = ref.dashboardId ? `/dashboards/${ref.dashboardId}${ref.tileId ? `?inspect=${encodeURIComponent(ref.tileId)}` : ""}` : null;
  const inUnits = (v: number) => (f === "pct" ? Math.round(v * 1000) / 10 : f === "money" ? Math.round(v) : Math.round(v * 10) / 10);
  return (
    <section className="card numcard" aria-label="The number behind this">
      <div className="card-head">
        <h2>The number behind this</h2>
        {href && <Link to={href}>Open on {ref.dashboardName ?? "the dashboard"}</Link>}
      </div>
      <div className="card-body">
        <p className="nc-what">{ref.title}</p>
        <div className="nc-grid">
          <div className="nc-cell">
            <span className="nc-l">When raised · {raised}</span>
            <span className="nc-v">{fmtPlain(ref.value, f)}</span>
            <span className="nc-s muted">{ref.periodFrom && ref.periodTo ? fmtRange(ref.periodFrom, ref.periodTo) : "All dates"}{ref.thenValue !== null && ref.value !== null && ref.compare ? ` · ${fmtSigned(ref.value - ref.thenValue, f)} against ${CMP[ref.compare]}` : ""}</span>
          </div>
          <div className="nc-cell">
            <span className="nc-l">Now</span>
            {ref.live ? <>
              <span className="nc-v">{fmtPlain(ref.live.value, f)}</span>
              <span className={`nc-s ${better === null ? "muted" : better ? "good" : "bad"}`}>{ref.live.from && ref.live.to ? `${fmtRange(ref.live.from, ref.live.to)} · ` : ""}{since === null || since === 0 ? "No change since raised" : `${fmtSigned(since, f)} since raised, ${better ? "better" : "worse"}`}</span>
            </> : <span className="nc-s muted">{ref.liveError ?? "Working it out…"}</span>}
          </div>
        </div>
        {ref.headline && <p className="nc-why"><b>Why, when it was raised.</b> {ref.headline}</p>}
        {onRecordResult && ref.live?.value !== null && ref.live?.value !== undefined && (
          <div className="nc-act">
            <button className="btn" disabled={busy || saved} onClick={async () => { setBusy(true); try { await onRecordResult(inUnits(ref.live!.value!)); setSaved(true); } finally { setBusy(false); } }}>
              {saved ? "Recorded" : `Record ${fmtPlain(ref.live.value, f)} as a result`}
            </button>
            <span className="muted">Today's number, from the same dashboard query.</span>
          </div>
        )}
      </div>
    </section>
  );
}
